/**
 * The slot grid, taken apart so two surfaces can be built from it.
 *
 * Chase's review of phase 5 named the slot grid as the thing he liked - "the
 * ones you have are highlighted and the ones that are not are faded, and you
 * have these little options to only look at the missing ones" - and then asked
 * why a chase binder does not look like that. A chase binder is the same kind
 * of object as a master set: a list of cards somebody is working toward. So the
 * gauge, the toolbar and the tile live here, and both surfaces draw the same
 * ones rather than two that agree until one of them is edited.
 *
 * WHAT IS SHARED IS THE LOOK, NOT THE DATA PATH, AND THAT IS DELIBERATE.
 *
 * A master set's slots come from the catalogue crossed with its printings, and
 * the ownership rule behind them lives in `public.wish_set_slot_rows` where it
 * is the database's single implementation. A chase binder's slots are simply
 * the rows a person put in it, from `public.wish_binder_cards`, so its
 * denominator is the binder's own size and its ownership is the plainer
 * question of whether they own that catalogue card at all. Pushing binder rows
 * through the set-slot RPC would force a printing model onto a list that has no
 * printings in it. Everything in this file is presentation: it is handed
 * numbers and tiles and draws them.
 */

import {
  Check, ChevronLeft, CircleDashed, HelpCircle, SlidersHorizontal,
} from 'lucide-react';
import ZoomStepIcon, { zoomLabel } from '../ZoomStepIcon';
import CardImage from '../CardImage';
import { TcgPocketChip } from '../TcgPocketNotice';
import { formatMoney } from '../../utils/catalogPrices';
import {
  describeCatalogFilters, hasCatalogFilters, countCatalogFilters, EMPTY_CATALOG_FILTERS,
} from '../../services/cardData/catalogFilters';
import haptics from '../../utils/haptics';

/**
 * The top of a slot grid: who it is, how far along, and the controls.
 *
 * Binder's own detail header is the shape Chase pointed at - back, name and
 * cover on one row, then `130 cards  $5,420.42` on the next, then cards. This
 * is the same two rows. The gauge used to own a full-width panel of its own
 * between the title and the toolbar, and that panel is what pushed the first
 * card row past the middle of the screen: measured off the device, a chase
 * binder spent 212pt of a 430pt phone before a single card, against the 124pt
 * Binder spends doing the same job.
 *
 * NOTHING WAS DROPPED TO WIN THAT BACK. The bar is a 6pt track riding beside
 * its own fraction on the title block's second line, which is how the chase
 * binder ROWS in the Wish tab have always drawn theirs, so the two readings of
 * one binder now agree in shape as well as in number. The zoom, the sort, the
 * missing-only toggle and the filter note share the second row instead of
 * taking one each.
 *
 * `progress.filled` of null still means nobody asked whose progress this is -
 * somebody else's list, where counting against the viewer's own cards would put
 * our Charizard under their name. That draws the size and no bar, the same as
 * it did when this was a panel. The noun is a prop because a master set fills
 * SLOTS, being (card, printing) pairs, and a chase binder fills CARDS; one word
 * for both would quietly claim a chase binder has printings in it.
 *
 * `meta` is what a surface says when it has nothing to fill - the master set
 * browser is being looked through rather than worked on, so it names the rung
 * instead of quoting a fraction.
 */
export function SlotGridHeader({
  title,
  onBack = null,
  backLabel = 'Back',
  progress = null,
  meta = null,
  hint = null,
  trailing = null,
  toolbar = null,
  theme,
  holdHandlers = null,
  onContextMenu = null,
  testId,
}) {
  const known = !!progress && progress.filled !== null && progress.filled !== undefined;
  const count = Number(progress?.total) || 0;
  const done = known ? Number(progress.filled) || 0 : 0;
  const pct = known && count > 0 ? Math.min(100, Math.round((done / count) * 100)) : 0;
  const noun = progress?.noun || 'slots';

  return (
    <div style={styles.header}>
      <div style={styles.identityRow}>
        {onBack && (
          <button
            style={{
              ...styles.back,
              backgroundColor: theme.bgCard,
              borderColor: theme.border,
              color: theme.text,
            }}
            onClick={onBack}
            aria-label={backLabel}
          >
            <ChevronLeft size={18} />
          </button>
        )}
        {/* The hold lives on the identity block rather than on a bar of its own,
            because the bar is no longer a box big enough to aim a thumb at. The
            block is the full height of the row, so the target grew. */}
        <div
          style={styles.identity}
          data-testid={testId}
          {...(holdHandlers || {})}
          onContextMenu={onContextMenu || undefined}
        >
          <span style={{ ...styles.title, color: theme.text }}>{title}</span>
          {progress ? (
            <span style={styles.gaugeLine}>
              {known && count > 0 && (
                <span style={{ ...styles.track, backgroundColor: theme.border }}>
                  <span style={{ ...styles.fill, width: `${pct}%`, backgroundColor: theme.warning }} />
                </span>
              )}
              {/* Full-strength text, not the muted token. This line is the only
                  place the size and the progress are said now, and `textMuted`
                  is #94a3b8 in BOTH themes - 7.7:1 on the dark background and
                  2.46:1 on the light one, which is a fail. The hierarchy against
                  the 17px title is carried by size and weight instead. */}
              <span style={{ ...styles.progressText, color: theme.text }}>
                {progress.pending && `Counting ${noun}...`}
                {!progress.pending && known && `${done} of ${count} ${noun} · ${pct}%`}
                {!progress.pending && !known && `${count} ${noun}`}
              </span>
            </span>
          ) : (
            meta && <span style={{ ...styles.metaText, color: theme.textSecondary }}>{meta}</span>
          )}
        </div>
        {trailing}
      </div>
      {toolbar}
      {hint && <span style={{ ...styles.headerHint, color: theme.textSecondary }}>{hint}</span>}
    </div>
  );
}

/**
 * Zoom, sort and filter, the one filter these grids exist for, and what is
 * currently narrowing them.
 *
 * Missing-only lives in the toolbar rather than inside the sort sheet because
 * "what am I still missing" is the question somebody opens one of these
 * surfaces to ask, and it is about their collection rather than about the
 * catalogue the sheet filters. It is hidden on somebody else's list, where
 * there is no ownership being drawn for it to narrow.
 *
 * The filter note rides at the end of this row rather than on a row below it.
 * Three controls leave better than a third of the row empty, and a note that
 * only appears when a filter is on was costing a whole line of the screen every
 * time somebody actually used one.
 */
export function SlotGridToolbar({
  zoom, onZoom,
  filters, onSort,
  missingOnly, onMissingOnly,
  readOnly = false,
  eras = [],
  onClearFilters = null,
  theme,
}) {
  return (
    <div style={styles.toolbar}>
      <button
        style={{
          ...styles.toolBtn,
          backgroundColor: zoom > 0 ? theme.accent : theme.bgSheet,
          borderColor: zoom > 0 ? theme.accent : theme.border,
          color: zoom > 0 ? '#fff' : theme.textSecondary,
        }}
        onClick={() => { haptics.select(); onZoom(); }}
        aria-label={zoomLabel(zoom)}
      >
        {/* The stepped glyph, not a static magnifier. Next to a search field a
            plain magnifier reads as a second search button. */}
        <ZoomStepIcon level={zoom} />
      </button>
      <button
        style={{
          ...styles.toolBtn,
          backgroundColor: hasCatalogFilters(filters) ? theme.accent : theme.bgSheet,
          borderColor: hasCatalogFilters(filters) ? theme.accent : theme.border,
          color: hasCatalogFilters(filters) ? '#fff' : theme.textSecondary,
        }}
        onClick={() => { haptics.select(); onSort(); }}
        aria-label="Sort and filter"
      >
        <SlidersHorizontal size={16} />
        {countCatalogFilters(filters) > 0 && (
          <span style={{ ...styles.toolBadge, backgroundColor: theme.warning }}>
            {countCatalogFilters(filters)}
          </span>
        )}
      </button>
      {!readOnly && (
        <button
          style={{
            ...styles.missingBtn,
            backgroundColor: missingOnly ? theme.warning : theme.bgSheet,
            borderColor: missingOnly ? theme.warning : theme.border,
            color: missingOnly ? '#000' : theme.textSecondary,
          }}
          onClick={() => { haptics.select(); onMissingOnly(); }}
          aria-pressed={missingOnly}
        >
          <CircleDashed size={15} />
          <span style={styles.missingLabel}>Missing only</span>
        </button>
      )}
      {onClearFilters && (
        <FilterNote filters={filters} eras={eras} theme={theme} onClear={onClearFilters} />
      )}
    </div>
  );
}

/** What is currently narrowing the grid, and one tap to stop it. */
export function FilterNote({ filters, eras = [], theme, onClear }) {
  if (!hasCatalogFilters(filters)) return null;
  return (
    <button
      style={{ ...styles.filterNote, backgroundColor: theme.bgCard, borderColor: theme.border }}
      onClick={() => { haptics.tap(); onClear(EMPTY_CATALOG_FILTERS); }}
    >
      <span style={{ ...styles.filterNoteText, color: theme.text }}>
        {describeCatalogFilters(filters, eras)}
      </span>
      <span style={{ ...styles.filterNoteClear, color: theme.purple }}>Clear</span>
    </button>
  );
}

/**
 * One slot.
 *
 * Owned is full colour; missing is faded rather than absent, because the whole
 * point of one of these grids is seeing the shape of the hole. The badge is on
 * the TOP edge and the price on the bottom, and they never swap: there is one
 * artwork per card and a printing has nowhere else to be said.
 */
export function SlotTile({ tile, captions, theme, isDark, pocket, pricingEnabled, neutral, onOpen, onHold }) {
  const dim = !neutral && !tile.owned;
  return (
    <button
      style={styles.tile}
      onClick={onOpen}
      aria-label={`${tile.name}, ${tile.badge?.full || 'slot'}`}
      {...(onHold || {})}
    >
      <div style={{
        ...styles.imgWrap,
        backgroundColor: theme.bgSheet,
        ...(dim ? styles.imgWrapDim : null),
      }}>
        <CardImage
          digitalImage={tile.imageUrl}
          name={tile.name}
          number={tile.localId}
          setName={tile.setName || tile.setId}
          imgStyle={styles.img}
          theme={theme}
          loading="lazy"
          thumb
        />
        {tile.badge && (
          <div style={{ ...styles.badge, backgroundColor: tile.badge.color }}>
            <span style={{ ...styles.badgeText, color: tile.badge.textColor }}>{tile.badge.label}</span>
          </div>
        )}
        {pocket && <TcgPocketChip theme={theme} isDark={isDark} small />}
        {/* Cost at every level, and now the cost of THIS printing.
            A price the slot could not find for its own printing falls back to
            the card's figure and is marked with a tilde, because a shadowless
            Charizard and a plain one are not the same object and quoting one
            number for both was the bug. */}
        {pricingEnabled && !pocket && formatMoney(tile.price) && (
          <div style={styles.price}>
            <span style={styles.priceText}>
              {tile.priceIsForPrinting ? '' : '~'}{formatMoney(tile.price)}
            </span>
          </div>
        )}
        {!neutral && tile.owned && (
          <div style={{ ...styles.ownedTag, backgroundColor: tile.printingUnknown ? theme.textSecondary : theme.accent }}>
            {tile.printingUnknown ? <HelpCircle size={10} color="#fff" /> : <Check size={10} color="#fff" />}
          </div>
        )}
      </div>
      {captions && (
        <>
          <span style={{ ...styles.name, color: theme.text }}>{tile.name}</span>
          <span style={{ ...styles.meta, color: theme.textSecondary }}>
            {tile.localId}
            {tile.badge ? ` · ${tile.badge.full}` : ''}
          </span>
        </>
      )}
    </button>
  );
}

export const slotGridStyles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '6px', width: '100%' },
  sentinel: { minHeight: '24px', display: 'flex', justifyContent: 'center' },
  state: { fontSize: '13px', margin: '16px 2px', lineHeight: 1.45 },
};

const styles = {
  // Two rows and an 8px seam, which is what the whole change comes down to.
  header: { display: 'flex', flexDirection: 'column', gap: '8px' },
  // 44 is the back button and the back button is the tallest thing on the row,
  // so the identity block sizes itself inside a target a thumb can already hit.
  identityRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  back: {
    width: '44px', height: '44px', borderRadius: '12px', border: '1px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    outline: 'none', flexShrink: 0, WebkitTapHighlightColor: 'transparent',
  },
  identity: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 },
  title: {
    fontSize: '17px', fontWeight: '700', lineHeight: 1.25,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  gaugeLine: { display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 },
  // The same 6px track the Wish tab's binder and set ROWS draw, so one binder
  // read two ways cannot look like two different kinds of progress.
  track: {
    flex: '1 1 auto', minWidth: '40px', maxWidth: '132px',
    height: '6px', borderRadius: '3px', overflow: 'hidden',
  },
  fill: { display: 'block', height: '100%', borderRadius: '3px' },
  progressText: {
    fontSize: '12px', fontWeight: '600', flexShrink: 0,
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  },
  metaText: {
    fontSize: '12px', lineHeight: 1.35,
    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  },
  headerHint: { fontSize: '11px', lineHeight: 1.35 },
  toolbar: { display: 'flex', alignItems: 'center', gap: '8px' },
  toolBtn: {
    position: 'relative', width: '44px', height: '44px', minWidth: '44px',
    borderRadius: '12px', border: '1px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    outline: 'none', flexShrink: 0, WebkitTapHighlightColor: 'transparent',
  },
  toolBadge: {
    position: 'absolute', top: '-4px', right: '-4px', minWidth: '16px', height: '16px',
    borderRadius: '8px', color: '#000', fontSize: '10px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
  },
  missingBtn: {
    // It never gives ground. Sharing the row with the filter note squeezed it
    // until "Missing only" broke over two lines and took the whole row to 60px,
    // which is the opposite of what this change is for. The note is the one
    // thing on the row that can be shortened, so the note does the shrinking.
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    flexShrink: 0, minHeight: '44px', padding: '0 12px', borderRadius: '12px', border: '1px solid',
    cursor: 'pointer', outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  missingLabel: { fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' },
  filterNote: {
    // It shares the control row now, so it takes the room the controls leave
    // rather than a line of its own, and it is a 44px target like they are.
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
    flex: '1 1 auto', minWidth: 0, minHeight: '44px',
    padding: '0 10px', borderRadius: '12px', border: '1px solid',
    cursor: 'pointer', textAlign: 'left', outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  filterNoteText: {
    fontSize: '12px', fontWeight: '600',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },
  filterNoteClear: { fontSize: '11px', fontWeight: '600', flexShrink: 0 },
  tile: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    background: 'transparent', border: 'none', padding: 0,
    textAlign: 'left', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },
  imgWrap: {
    position: 'relative', width: '100%', aspectRatio: '5 / 7',
    borderRadius: '8px', overflow: 'hidden',
  },
  // Faded rather than hidden, which is the whole point of a slot grid: the
  // shape of what is missing is the thing somebody came to look at.
  imgWrapDim: { opacity: 0.45, filter: 'grayscale(0.5)' },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  badge: {
    position: 'absolute', top: '3px', left: '3px', right: '3px',
    padding: '2px 4px', borderRadius: '4px', textAlign: 'center', overflow: 'hidden',
  },
  badgeText: {
    fontSize: '9px', fontWeight: '700', letterSpacing: '0.2px',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
  },
  price: {
    position: 'absolute', bottom: '4px', right: '4px',
    padding: '2px 5px', borderRadius: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    maxWidth: 'calc(100% - 8px)', overflow: 'hidden',
  },
  priceText: {
    fontSize: '9px', fontWeight: '700', color: '#4ade80',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  },
  ownedTag: {
    position: 'absolute', bottom: '4px', left: '4px',
    width: '16px', height: '16px', borderRadius: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  name: {
    fontSize: '12px', fontWeight: '650', lineHeight: 1.25, marginTop: '6px',
    overflow: 'hidden', textOverflow: 'ellipsis',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  },
  meta: {
    fontSize: '10.5px', lineHeight: 1.25, marginTop: '2px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
  },
};
